"use client"

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TaxCalculation } from './TaxCalculation'
import { useToast } from '@/hooks/use-toast'
import TaxesService from '@/api/taxes.service'
import { Loader2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { TaxSearchField } from "./TaxSearchField"
import { Plus, Minus, Trash2, AlertCircle } from "lucide-react"

interface Tax {
  id: number
  taxName: string
  taxRate: number
  country?: string
  type?: string
  code?: string
  taxAmount?: number
}

interface InvoiceTaxSectionProps {
  subtotal: number
  currency: string
  businessId?: number
  customerId?: number
  quotationId?: number
  initialTaxes?: Tax[]
  onTaxesChange: (taxes: Tax[]) => void
  className?: string
  isInclusive?: boolean
  onInclusiveChange?: (isInclusive: boolean) => void
}

export function InvoiceTaxSection({
  subtotal,
  currency,
  businessId,
  customerId,
  quotationId,
  initialTaxes = [],
  onTaxesChange,
  className = "",
  isInclusive = false,
  onInclusiveChange
}: InvoiceTaxSectionProps) {
  const [taxes, setTaxes] = useState<Tax[]>(initialTaxes)
  const [businessTaxInfos, setBusinessTaxInfos] = useState<any[]>([])
  const [showBusinessTaxInfo, setShowBusinessTaxInfo] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (businessId) {
      fetchBusinessTaxInfos(businessId)
    }
  }, [businessId])

  const fetchBusinessTaxInfos = async (id: number) => {
    setIsLoading(true)
    try {
      const response = await TaxesService.getBusinessTaxInfos(id)
      if (response.data.result === 'success') {
        setBusinessTaxInfos(response.data.businessTaxInfos || [])
      }
    } catch (error) {
      console.error('Error fetching business tax information:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // Update tax amounts when subtotal or taxes change
    const updatedTaxes = taxes.map(tax => ({
      ...tax,
      taxAmount: (subtotal * tax.taxRate) / 100
    }))
    
    setTaxes(updatedTaxes)
    onTaxesChange(updatedTaxes)
  }, [subtotal, taxes.length, taxes.map(t => t.id).join(',')])

  const handleAddTax = (newTax: Tax) => {
    const taxWithAmount = {
      ...newTax,
      taxAmount: (subtotal * newTax.taxRate) / 100
    }
    
    const updatedTaxes = [...taxes, taxWithAmount]
    setTaxes(updatedTaxes)
    onTaxesChange(updatedTaxes)
  }

  const handleRemoveTax = (index: number) => {
    const updatedTaxes = taxes.filter((_, i) => i !== index)
    setTaxes(updatedTaxes)
    onTaxesChange(updatedTaxes)
  }

  const handleInclusiveChange = (checked: boolean) => {
    if (onInclusiveChange) {
      onInclusiveChange(checked)
    }
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-medium flex justify-between items-center">
          Taxes
          <div className="flex items-center space-x-2">
            <Switch 
              id="tax-inclusive" 
              checked={isInclusive} 
              onCheckedChange={handleInclusiveChange}
            />
            <Label htmlFor="tax-inclusive" className="text-sm font-normal">
              Tax Inclusive
            </Label>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {businessTaxInfos.length > 0 && showBusinessTaxInfo && (
          <div className="rounded-md border p-4 bg-muted/30">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium mb-2">Business Tax Information</h4>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 w-6 p-0" 
                onClick={() => setShowBusinessTaxInfo(false)}
              >
                <Minus className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {businessTaxInfos.map((taxInfo) => (
                <div key={taxInfo.id} className="text-sm flex items-center border-b pb-2">
                  <div className="font-medium mr-2">{taxInfo.taxType.name}:</div>
                  <div className="text-muted-foreground">{taxInfo.taxIdentifier}</div>
                  {taxInfo.isVerified && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Verified
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {businessTaxInfos.length > 0 && !showBusinessTaxInfo && (
          <Alert className="bg-muted/10 cursor-pointer" onClick={() => setShowBusinessTaxInfo(true)}>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle className="text-sm font-medium">Business Tax Information Available</AlertTitle>
            <AlertDescription className="text-xs">
              Click to view tax information
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label className="text-sm">Add Taxes</Label>
          <TaxSearchField 
            onSelect={handleAddTax}
            selectedTaxes={taxes}
            businessId={businessId}
            placeholder="Search for taxes..."
            showRecent
            showQuickAdd
          />
        </div>

        {taxes.length > 0 ? (
          <div className="space-y-3">
            <Label className="text-sm">Applied Taxes</Label>
            {taxes.map((tax, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-md bg-background">
                <div>
                  <div className="font-medium">{tax.taxName}</div>
                  <div className="text-sm text-muted-foreground">{tax.taxRate}%</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-medium">{currency} {(tax.taxAmount || 0).toFixed(2)}</div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleRemoveTax(index)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 border border-dashed rounded-md">
            <p className="text-sm text-muted-foreground mb-2">No taxes added yet</p>
            <p className="text-xs text-muted-foreground mb-4">Search for taxes to add them to this invoice</p>
          </div>
        )}

        <div className="py-2 border-t">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Total Tax Amount:</span>
            <span className="font-medium">
              {currency} {taxes.reduce((acc, tax) => acc + (tax.taxAmount || 0), 0).toFixed(2)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 